import { useLazyQuery } from "@apollo/client";
import { useEffect, useMemo } from "react";
import { GET_CUSTOM_FIELD_DATA } from "graphql/customFieldData";
import { GET_CUSTOM_FIELD_SETTINGS } from "../graphql/customFieldSetting";
import { SummaryItem } from "components/common/Summary";
import CustomFieldParentType from "enums/CustomFieldParentType";
import CustomFieldStatus from "enums/CustomFieldStatus";
import CustomFieldType from "enums/CustomFieldType";
import {
  CustomFieldsRecord,
  FormikCustomField,
  SimplifiedCustomField,
} from "./customFields.types";
import { isCustomFieldVisible } from "./customFields.utils";
import useCustomFieldAllocation from "./useCustomFieldAllocation";

type UseCustomFieldRendererParams = {
  parentType?: CustomFieldParentType;
  inheritFrom?: {
    type: CustomFieldParentType;
    id: string;
  };
  customFields?: CustomFieldsRecord; // Pass to drive filtered display outputs
};

// Hook for consumers that need to render, validate, or summarize custom field
// VALUES on a domain form (user, card, payment, contact, invoice,
// reimbursement). Fetches the active settings for the given parentType,
// optionally hydrates from a parent entity (inheritFrom), and exposes display
// helpers (filteredCustomFields, summary items, hasVisibleCustomFields) plus
// the pure transforms used to move between record and array shape.
//
// Calling with `{}` is supported — you get back only the pure transforms and
// no queries fire.
const useCustomFieldRenderer = ({
  parentType,
  inheritFrom,
  customFields,
}: UseCustomFieldRendererParams) => {
  // ============================================
  // HELPERS
  // ============================================

  const initializeCustomFields = (
    customFieldSettings: FormikCustomField[],
  ): CustomFieldsRecord => {
    return customFieldSettings.reduce((acc: CustomFieldsRecord, field) => {
      acc[field.id!] = [""];
      return acc;
    }, {});
  };

  const transformCustomFieldsToArray = (
    customFieldsObject: CustomFieldsRecord | undefined,
  ) => {
    if (!customFieldsObject || Object.keys(customFieldsObject).length === 0) {
      return [];
    }
    return Object.entries(customFieldsObject).map(
      ([fieldSettingId, values]) => ({
        fieldSettingId,
        values,
      }),
    );
  };

  // used to set formik initial values from array data and set default values
  const transformCustomFieldsToRecord = (
    customFieldsArray?: any,
    customFieldSettings?: FormikCustomField[],
  ): CustomFieldsRecord => {
    if (!Array.isArray(customFieldsArray) || customFieldsArray.length === 0) {
      if (customFieldSettings) {
        return initializeCustomFields(customFieldSettings);
      }
      return {};
    }

    const existingValues = customFieldsArray.reduce(
      (
        acc: CustomFieldsRecord,
        field: { fieldSettingId: string; values: any },
      ) => {
        acc[field.fieldSettingId] = field.values;
        return acc;
      },
      {},
    );

    if (!customFieldSettings) {
      return existingValues;
    }

    const initializedFields = initializeCustomFields(customFieldSettings);

    const combinedData = {
      ...initializedFields,
      ...existingValues,
    };
    return combinedData;
  };

  const renderCustomFieldDisplayValue = (
    field: FormikCustomField,
    cleanedValues: string[],
  ): string => {
    switch (field.type) {
      case CustomFieldType.ALLOCATION:
        // Drop unresolved ids: when a user just selected a new allocation,
        // its name lookup may not have landed yet — better to briefly show
        // nothing than "undefined" or a raw UUID.
        return cleanedValues
          .map((id: string) => allocationMap[id])
          .filter(Boolean)
          .join(", ");

      default:
        return cleanedValues.join(", ");
    }
  };

  const mergeCustomFieldDataWithSettings = (
    customFieldSettings: FormikCustomField[],
    customFieldData: CustomFieldsRecord,
    currentValues?: CustomFieldsRecord, // optional pass in to preserve user input
  ) => {
    const initializedFields = initializeCustomFields(customFieldSettings);

    return customFieldSettings.reduce(
      (acc: CustomFieldsRecord, field) => {
        if (!field.id) return acc;

        if (currentValues) {
          const current = currentValues[field.id];
          const inherited = customFieldData[field.id];

          const isEmpty =
            !current ||
            current.length === 0 ||
            (current.length === 1 && current[0] === "");

          if (isEmpty && inherited && inherited.length > 0) {
            acc[field.id] = inherited;
          } else if (current) {
            acc[field.id] = current; // Preserve user input
          } else {
            acc[field.id] = initializedFields[field.id]; // Default to ['']
          }
        } else if (customFieldData[field.id]) {
          acc[field.id] = customFieldData[field.id];
        }
        // If no data, keep the [''] from initializedFields

        return acc;
      },
      { ...initializedFields },
    );
  };

  // ============================================
  // QUERIES
  // ============================================

  const [
    getCustomFieldSettings,
    { data: customFieldSettingsUnclean, loading: loadingCustomFields },
  ] = useLazyQuery(GET_CUSTOM_FIELD_SETTINGS, {
    fetchPolicy: "cache-and-network",
  });

  const [
    getInheritedData,
    { data: rawInheritedData, loading: loadingInheritedData },
  ] = useLazyQuery(GET_CUSTOM_FIELD_DATA, {
    fetchPolicy: "cache-and-network",
  });

  // ============================================
  // DERIVED STATE
  // ============================================

  const customFieldSettings: FormikCustomField[] = useMemo(
    () =>
      customFieldSettingsUnclean?.customFieldSettings?.edges.map(
        (edge: { node: SimplifiedCustomField }) => edge.node,
      ) || [],
    [customFieldSettingsUnclean],
  );

  const customFieldsInitialValues: CustomFieldsRecord = useMemo(() => {
    // If no settings yet, return empty
    if (customFieldSettings.length === 0) return {};

    // Initialize with empty strings
    const emptyFields = initializeCustomFields(customFieldSettings);

    // If no inheritance needed, return empty
    if (!inheritFrom?.id || !rawInheritedData?.getCustomFieldData) {
      return emptyFields;
    }

    // Merge inherited data with settings structure
    const inheritedData = transformCustomFieldsToRecord(
      rawInheritedData.getCustomFieldData,
    );

    return mergeCustomFieldDataWithSettings(customFieldSettings, inheritedData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFieldSettings, inheritFrom?.id, rawInheritedData]);

  const { allocationMap, loading: loadingAllocations } =
    useCustomFieldAllocation(customFieldSettings, customFields);

  const filteredCustomFields = useMemo(() => {
    if (!customFields) return [];

    return customFieldSettings
      .filter((field) => {
        const fieldValues = customFields[field.id!] || [];

        return fieldValues.some((value: string) => value.trim() !== "");
      })
      .map((field) => {
        const fieldValues = customFields[field.id!] || [];
        const cleanedValues = fieldValues.filter(
          (value: string) => value.trim() !== "",
        );

        const displayValue = renderCustomFieldDisplayValue(
          field,
          cleanedValues,
        );

        return {
          id: field.id,
          label: field.label,
          value: displayValue,
        };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customFieldSettings, customFields, allocationMap]);

  const filteredCustomFieldSummaryItems: SummaryItem[] =
    filteredCustomFields.map((field) => ({
      title: field.label,
      value: field.value,
    }));

  const hasVisibleCustomFields = useMemo(() => {
    return customFieldSettings.some((field) =>
      isCustomFieldVisible(field, customFields?.[field.id!] || []),
    );
  }, [customFieldSettings, customFields]);

  // ============================================
  // EFFECTS
  // ============================================

  useEffect(() => {
    if (parentType) {
      getCustomFieldSettings({
        variables: {
          parentType: parentType,
          status: [CustomFieldStatus.ACTIVE, CustomFieldStatus.READONLY],
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [parentType]);

  useEffect(() => {
    if (inheritFrom?.id && inheritFrom?.type) {
      getInheritedData({
        variables: {
          parentId: inheritFrom.id,
          parentType: inheritFrom.type,
        },
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inheritFrom?.id, inheritFrom?.type]);

  // ============================================
  // LOADING STATES
  // ============================================

  const specificLoadingStates = {
    loadingCustomFields,
    loadingInheritedData,
    loadingAllocations,
  };

  const isAnyLoading = Object.values(specificLoadingStates).some(
    (loading) => loading,
  );

  return {
    customFieldSettings,
    customFieldsInitialValues,
    filteredCustomFields,
    filteredCustomFieldSummaryItems,
    hasVisibleCustomFields,
    specificLoadingStates,
    isAnyLoading,
    transformCustomFieldsToArray,
    transformCustomFieldsToRecord,
    mergeCustomFieldDataWithSettings,
  };
};

export default useCustomFieldRenderer;
